import { upgradeTargetName } from "@highmarch/app";
import { BalanceData, PlayerProfile, UpgradeJob, UpgradeTarget } from "@highmarch/domain";
import { btnHtml, ribbonHtml } from "../kit";
import { icon } from "../iconsKit";
import { portraitUrl, uiUrl } from "../portraits";
import { petModelName } from "../../render/skins/kits/pets";

/**
 * "While you were away" (engagement doc §B5): on entering the hub, one card lists what happened since the player
 * last had the game open — the upgrades that finished (with the tower they raised) and whatever other return features
 * add through `registerAwayItems` (Daily Orders' refresh, for one) — and how long they were gone.
 */
export interface AwayItem {
  /** A kit icon name, or an image URL (a tower portrait). */
  readonly icon: string;
  readonly title: string;
  readonly detail?: string;
}

export interface AwayContext {
  /** Seconds since the page was last seen (null when unknown: a first launch). */
  readonly awaySeconds: number | null;
  /** Clock seconds now. */
  readonly now: number;
}

/** A return feature's contribution to the card; returns nothing when it has nothing to say. */
export type AwayItemSource = (ctx: AwayContext) => readonly AwayItem[];

const sources: AwayItemSource[] = [];

/** The extension point: another feature adds its lines to the card. Returns the unregister. */
export function registerAwayItems(source: AwayItemSource): () => void {
  sources.push(source);
  return () => { const i = sources.indexOf(source); if (i >= 0) sources.splice(i, 1); };
}

export function collectAwayItems(ctx: AwayContext): AwayItem[] {
  return sources.flatMap((s) => [...s(ctx)]);
}

/** Below this the time away is not worth a line (a reload, a quick app switch). */
const MIN_AWAY_SECONDS = 60;
const MINUTE = 60, HOUR = 3600, DAY = 86400;

/** "2 days", "3 h 12 min", "25 min"; null under MIN_AWAY_SECONDS or when unknown. */
export function fmtAway(seconds: number | null): string | null {
  if (seconds === null || seconds < MIN_AWAY_SECONDS) return null;
  if (seconds >= DAY) { const d = Math.floor(seconds / DAY); return `${d} day${d === 1 ? "" : "s"}`; }
  if (seconds >= HOUR) { const h = Math.floor(seconds / HOUR), m = Math.floor((seconds % HOUR) / MINUTE); return m > 0 ? `${h} h ${m} min` : `${h} h`; }
  return `${Math.floor(seconds / MINUTE)} min`;
}

/** The name of what a job upgraded: the fortress, a tower type or a pet (app upgradeTargetName). */
export function jobTargetName(balance: BalanceData, j: UpgradeJob): string { return upgradeTargetName(balance, j.target, j.towerIndex); }

/** One line for the finished upgrades, for a toast ("Archer reached level 5 · Fortress reached level 3"). */
export function jobsDoneText(balance: BalanceData, jobs: readonly UpgradeJob[]): string {
  return jobs.map((j) => `${jobTargetName(balance, j)} reached level ${j.toLevel}`).join(" · ");
}

export function jobAwayItems(balance: BalanceData, jobs: readonly UpgradeJob[]): AwayItem[] {
  return jobs.map((j) => {
    const pet = j.target === UpgradeTarget.Pet ? balance.pets.list[j.towerIndex] : undefined;
    const art = pet ? uiUrl(`pets/${petModelName(pet.id)}_256.png`) : portraitUrl(j.target === UpgradeTarget.Base ? "base" : balance.towers[j.towerIndex].id);
    return { icon: art ?? "tower", title: `${jobTargetName(balance, j)} upgraded`, detail: `Level ${j.toLevel - 1} → ${j.toLevel}` };
  });
}

/**
 * The home-screen badge: builders standing idle with an upgrade they could start now — the free builders, capped by
 * how many targets (the fortress, owned towers, pets caught) have an affordable next level on their own.
 */
export function idleBuilderBadge(balance: BalanceData, profile: PlayerProfile, owned: readonly boolean[]): number {
  if (profile.freeBuilders <= 0) return 0;
  let affordable = 0;
  balance.towers.forEach((t, i) => {
    if (!t.isBase && !owned[i]) return;
    const target = t.isBase ? UpgradeTarget.Base : UpgradeTarget.Tower;
    const level = t.isBase ? profile.baseLevel : profile.towerLevels[i];
    if (profile.isUpgrading(target, i) || level >= balance.tables.maxLevel) return;
    if (profile.canAfford(profile.costFor(level + 1))) affordable++;
  });
  // Pets v2: a pet caught is upgraded by the same builders, under its level cap
  balance.pets.list.forEach((_, i) => {
    const level = profile.levelOf(UpgradeTarget.Pet, i);
    if (level <= 0 || level >= profile.upgradeCap(UpgradeTarget.Pet, i) || profile.isUpgrading(UpgradeTarget.Pet, i)) return;
    if (profile.canAfford(profile.costFor(level + 1))) affordable++;
  });
  return Math.min(profile.freeBuilders, affordable);
}

/** The card itself: a parchment dialog over a scrim; resolves when closed. */
export function showAwayCard(parent: HTMLElement, items: readonly AwayItem[], awaySeconds: number | null): Promise<void> {
  return new Promise((resolve) => {
    const away = fmtAway(awaySeconds);
    const scrim = document.createElement("div");
    scrim.className = "scrim awayscrim";
    const row = (it: AwayItem) => {
      const pic = /^(\.|\/|data:|blob:|https?:)/.test(it.icon) ? `<img decoding="sync" src="${it.icon}" alt="">` : icon(it.icon, 26);
      return `<li><span class="pic">${pic}</span><span class="tx"><b>${it.title}</b>${it.detail ? `<small>${it.detail}</small>` : ""}</span></li>`;
    };
    scrim.innerHTML = `<div class="panel dialog awaycard">${ribbonHtml("WHILE YOU WERE AWAY", "", "dtitle disp")}
      ${away ? `<p class="awayfor">${icon("clock", 14)} Away ${away}</p>` : ""}
      <ul>${items.map(row).join("")}</ul>
      ${btnHtml("NICE", "gold", { className: "ok disp accent" })}</div>`;
    const done = () => { scrim.remove(); resolve(); };
    scrim.querySelector<HTMLButtonElement>(".ok")!.onclick = done;
    scrim.onclick = (e) => { if (e.target === scrim) done(); };
    parent.append(scrim);
  });
}
